import { z } from 'zod';
import { CaseIdSchema, IsoTimestampSchema, SourceIdSchema, SourceItemIdSchema } from './common.js';

export const StartImportInputSchema = z.object({
  caseId: CaseIdSchema,
  sourceId: SourceIdSchema,
});
export type StartImportInput = z.infer<typeof StartImportInputSchema>;

export const DeleteSourceInputSchema = z.object({
  caseId: CaseIdSchema,
  sourceId: SourceIdSchema,
});
export type DeleteSourceInput = z.infer<typeof DeleteSourceInputSchema>;

export const CreateWhatsappUploadInputSchema = z.object({
  caseId: CaseIdSchema,
  navn: z.string().min(1).default('WhatsApp'),
  fileName: z.string().min(1).optional(),
});
export type CreateWhatsappUploadInput = z.infer<typeof CreateWhatsappUploadInputSchema>;

export const CreateWhatsappUploadResultSchema = z.object({
  sourceId: SourceIdSchema,
  uploadObjectPath: z.string().min(1),
  uploadContentType: z.string().min(1),
});
export type CreateWhatsappUploadResult = z.infer<typeof CreateWhatsappUploadResultSchema>;

export const CreatePstUploadInputSchema = z.object({
  caseId: CaseIdSchema,
  navn: z.string().min(1).default('Outlook PST'),
  fileName: z.string().min(1).optional(),
});
export type CreatePstUploadInput = z.infer<typeof CreatePstUploadInputSchema>;

export const CreatePstUploadResultSchema = z.object({
  sourceId: SourceIdSchema,
  uploadObjectPath: z.string().min(1),
  uploadContentType: z.string().min(1),
});
export type CreatePstUploadResult = z.infer<typeof CreatePstUploadResultSchema>;

/**
 * Kilde-uavhengig opplastingsmetadata. Lagres på `source.metadata.upload` og
 * leses av importerens streaming-opplastingsrute og kilde-importørene, slik at
 * alle fil-baserte kilder deler samme opplastingskanal.
 */
export const SourceUploadMetadataSchema = z.object({
  objectPath: z.string().min(1),
  contentType: z.string().min(1),
});
export type SourceUploadMetadata = z.infer<typeof SourceUploadMetadataSchema>;

// ---------------------------------------------------------------------------
// iPhone SMS/iMessage-import (kilde-type `iphone_backup`)
// ---------------------------------------------------------------------------

export const CreateIphoneSmsUploadInputSchema = z.object({
  caseId: CaseIdSchema,
  navn: z.string().min(1).default('SMS/iMessage'),
  fileName: z.string().min(1).optional(),
});
export type CreateIphoneSmsUploadInput = z.infer<typeof CreateIphoneSmsUploadInputSchema>;

export const CreateIphoneSmsUploadResultSchema = z.object({
  sourceId: SourceIdSchema,
  uploadObjectPath: z.string().min(1),
  uploadContentType: z.string().min(1),
});
export type CreateIphoneSmsUploadResult = z.infer<typeof CreateIphoneSmsUploadResultSchema>;

/**
 * Sammendrag av én samtale (chat) i en iPhone-backup. Bygges av importeren
 * under preview-steget og lagres på `source.metadata.iphoneSms.conversations`
 * slik at frontend kan vise samtaleliste med datofilter og utvalg før import.
 */
export const IphoneSmsConversationSchema = z.object({
  chatId: z.string().min(1),
  chatIdentifier: z.string().optional(),
  displayName: z.string(),
  participants: z.array(z.string()),
  messageCount: z.number().int().nonnegative(),
  firstMessageAt: IsoTimestampSchema.optional(),
  lastMessageAt: IsoTimestampSchema.optional(),
  attachmentCount: z.number().int().nonnegative(),
  service: z.enum(['sms', 'imessage', 'mms', 'rcs', 'unknown']),
});
export type IphoneSmsConversation = z.infer<typeof IphoneSmsConversationSchema>;

export const StartIphoneSmsImportInputSchema = z.object({
  caseId: CaseIdSchema,
  sourceId: SourceIdSchema,
  conversationIds: z.array(z.string().min(1).max(256)).min(1).max(2000),
  // Firebase callable-SDK koder utelatte felter som `null`, så vi godtar både
  // `null` og `undefined` for de valgfrie datofiltrene.
  dateFrom: IsoTimestampSchema.nullish(),
  dateTo: IsoTimestampSchema.nullish(),
});
export type StartIphoneSmsImportInput = z.infer<typeof StartIphoneSmsImportInputSchema>;

// ---------------------------------------------------------------------------
// Lokal multifil-dokumentopplasting (kilde-type `document_upload`)
// ---------------------------------------------------------------------------

export const CreateDocumentUploadInputSchema = z.object({
  caseId: CaseIdSchema,
  navn: z.string().min(1).default('Dokumenter'),
});
export type CreateDocumentUploadInput = z.infer<typeof CreateDocumentUploadInputSchema>;

export const CreateDocumentUploadResultSchema = z.object({
  sourceId: SourceIdSchema,
});
export type CreateDocumentUploadResult = z.infer<typeof CreateDocumentUploadResultSchema>;

export const GetAttachmentDownloadUrlInputSchema = z.object({
  caseId: CaseIdSchema,
  sourceItemId: SourceItemIdSchema,
  disposition: z.enum(['inline', 'attachment']).default('inline'),
});
export type GetAttachmentDownloadUrlInput = z.infer<typeof GetAttachmentDownloadUrlInputSchema>;

export const GetAttachmentDownloadUrlResultSchema = z.object({
  url: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1),
  expiresAt: IsoTimestampSchema,
});
export type GetAttachmentDownloadUrlResult = z.infer<typeof GetAttachmentDownloadUrlResultSchema>;

export const DocumentUploadFileSchema = z.object({
  objectPath: z.string().min(1),
  fileName: z.string().min(1),
  contentType: z.string().min(1),
  size: z.number().int().nonnegative(),
});
export type DocumentUploadFile = z.infer<typeof DocumentUploadFileSchema>;

/**
 * Metadata for lokal multifil-dokumentopplasting. Lagres på
 * `source.metadata.documentUpload` og beskriver opplastede filer samt om ZIP-er
 * skal pakkes ut under import.
 */
export const DocumentUploadMetadataSchema = z.object({
  files: z.array(DocumentUploadFileSchema).default([]),
  expandZips: z.boolean().default(true),
});
export type DocumentUploadMetadata = z.infer<typeof DocumentUploadMetadataSchema>;
